/* Humbler product page: gallery (+ zoom), variant switching from the embedded product JSON, offer
   pickers (2 for $99 behind the "Add a second item" switch, 3 chains for $99) with a preselected partner, the
   Mystery add-on (when the section shows it), the
   "What's included" value stack, add-to-bag via window.HumblerCart and the floating mobile
   add-to-bag. Engraving products (the custom dog tag) add through <humbler-engraving>
   (assets/humbler-engraving.js): the engraving is required and goes on the line as its property.
   Also drives the progress bars of the product rails sections. Works natively and in
   the local preview; no section-rendering fetch. */
(() => {
  if (window.HumblerProduct) return;
  window.HumblerProduct = true;

  const ui = () => window.HumblerUI || {};
  const money = cents => (ui().money ? ui().money(cents) : `$${(Number(cents) / 100).toFixed(Number(cents) % 100 ? 2 : 0)}`);
  // Offer prices in the product JSON are USD theme settings; variant prices are in the shopper's currency.
  const fromUsd = cents => (ui().fromUsd ? ui().fromUsd(cents) : Number(cents) || 0);
  const isPreview = () => (ui().isPreview ?? document.documentElement.dataset.humblerPreview === 'true');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 1000px)');
  const fill = (template, values) => String(template || '').replace(/\{(\w+)\}|%(\w+)%/g, (_, brace, pct) => (values[brace || pct] ?? ''));
  const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random = Math.random() * 16 | 0;
    return (char === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  }));
  const openDialog = (dialog, trigger) => {
    if (!dialog) return;
    if (ui().openDialog) ui().openDialog(dialog, trigger);
    else if (!dialog.open) dialog.showModal();
  };
  const closeDialog = dialog => {
    if (!dialog) return;
    if (ui().closeDialog) ui().closeDialog(dialog);
    else if (dialog.open) dialog.close();
  };
  const anyDialogOpen = () => !!document.querySelector('dialog[open]');

  /* Scroller progress bar (hb-scroller + hb-scroller-progress). */
  function bindProgress(list, bar) {
    if (!list || !bar) return () => {};
    const track = bar.parentElement;
    const update = () => {
      const max = list.scrollWidth - list.clientWidth;
      track.hidden = max <= 4;
      if (max <= 4) return;
      const width = Math.max(8, (list.clientWidth / list.scrollWidth) * 100);
      const ratio = Math.min(1, Math.max(0, list.scrollLeft / max));
      bar.style.setProperty('--_width', `${width}%`);
      bar.style.setProperty('--_offset', `${ratio * ((100 - width) / width) * 100}%`);
    };
    list.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();
    return update;
  }

  /* ---------- gallery ---------- */
  function initGallery(root) {
    const gallery = root.querySelector('[data-hb-gallery]');
    const track = gallery?.querySelector('[data-hb-gallery-track]');
    if (!track) return { showMedia() {} };
    const slides = [...track.querySelectorAll('[data-hb-slide]')];
    const dots = [...gallery.querySelectorAll('[data-hb-dot]')];
    let active = Math.min(Number(gallery.dataset.start) || 0, Math.max(0, slides.length - 1));

    const setActive = index => {
      active = index;
      slides.forEach((slide, position) => slide.classList.toggle('is-active', position === index));
      dots.forEach((dot, position) => (position === index ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current')));
    };
    const go = (index, smooth = true) => {
      const target = Math.max(0, Math.min(index, slides.length - 1));
      if (!desktop.matches) track.scrollTo({ left: target * track.clientWidth, behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto' });
      setActive(target);
    };

    if (active > 0 && !desktop.matches) requestAnimationFrame(() => { track.scrollLeft = active * track.clientWidth; });
    // A tab stop only while the track is a carousel (phones, more than one image): on desktop it is a static
    // grid of images, and a focus stop there would do nothing (and shows no ring).
    const syncFocusable = () => {
      if (desktop.matches || slides.length < 2) track.removeAttribute('tabindex');
      else track.tabIndex = 0;
    };
    syncFocusable();
    desktop.addEventListener('change', syncFocusable);
    let frame = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (desktop.matches || !track.clientWidth) return;
        const index = Math.round(track.scrollLeft / track.clientWidth);
        if (index !== active) setActive(index);
      });
    }, { passive: true });
    dots.forEach(dot => dot.addEventListener('click', () => go(Number(dot.dataset.hbDot))));
    track.addEventListener('keydown', event => {
      if (desktop.matches || event.target !== track) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); go(active + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); go(active - 1); }
    });

    /* tap to zoom. Phones: every image stacked, scrolled to the tapped one. Desktop: one image at a
       time with arrows, thumbnails and the keyboard; click the image to magnify it under the pointer. */
    const zoom = gallery.querySelector('[data-hb-zoom-dialog]');
    if (zoom) {
      const body = zoom.querySelector('[data-hb-zoom-body]');
      const items = [...zoom.querySelectorAll('[data-hb-zoom-item]')];
      const thumbs = [...zoom.querySelectorAll('[data-hb-zoom-go]')];
      const count = zoom.querySelector('[data-hb-zoom-count]');
      let current = 0;
      const setOrigin = (item, event) => {
        const box = item.getBoundingClientRect();
        item.style.setProperty('--hb-zoom-x', `${Math.min(100, Math.max(0, (event.clientX - box.left) / box.width * 100))}%`);
        item.style.setProperty('--hb-zoom-y', `${Math.min(100, Math.max(0, (event.clientY - box.top) / box.height * 100))}%`);
      };
      const show = index => {
        if (!items.length) return;
        current = (index + items.length) % items.length;
        items.forEach((item, position) => { item.classList.toggle('is-active', position === current); item.classList.remove('is-zoomed'); });
        thumbs.forEach((thumb, position) => thumb.setAttribute('aria-current', String(position === current)));
        if (count) count.textContent = `${current + 1} / ${items.length}`;
        thumbs[current]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      };
      track.addEventListener('click', event => {
        const button = event.target.closest('[data-hb-zoom]');
        if (!button) return;
        const index = Math.max(0, items.findIndex(item => item.dataset.hbZoomItem === button.dataset.hbZoom));
        // Until a large file is in, its frame shows the gallery's own (already loaded) image underneath,
        // framed the same way (contain), instead of an empty stage.
        items.forEach(item => {
          const large = item.querySelector('img');
          const small = slides[Number(item.dataset.hbZoomItem)]?.querySelector('img');
          if (!large || large.complete || !small?.complete || !small.currentSrc || item.style.backgroundImage) return;
          const preview = { 'background-image': `url("${small.currentSrc.replace(/"/g, '%22')}")`, 'background-size': 'contain', 'background-position': 'center', 'background-repeat': 'no-repeat', animation: 'none' };
          Object.entries(preview).forEach(([name, value]) => item.style.setProperty(name, value));
          const done = () => Object.keys(preview).forEach(name => item.style.removeProperty(name));
          large.addEventListener('load', done, { once: true });
          large.addEventListener('error', done, { once: true });
        });
        zoom.querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = 'eager'; });
        show(index);
        openDialog(zoom, button);
        requestAnimationFrame(() => { if (!desktop.matches && items[index] && body) body.scrollTop = items[index].offsetTop; });
      });
      zoom.addEventListener('click', event => {
        const step = event.target.closest('[data-hb-zoom-step]');
        if (step) { show(current + Number(step.dataset.hbZoomStep)); return; }
        const thumb = event.target.closest('[data-hb-zoom-go]');
        if (thumb) { show(Number(thumb.dataset.hbZoomGo)); return; }
        const item = desktop.matches && event.target.closest('.hb-pdp-zoom__item.is-active');
        if (item) { setOrigin(item, event); item.classList.toggle('is-zoomed'); }
      });
      zoom.addEventListener('pointermove', event => {
        const item = event.target.closest('.hb-pdp-zoom__item.is-zoomed');
        if (item) setOrigin(item, event);
      });
      zoom.addEventListener('keydown', event => {
        if (!desktop.matches || items.length < 2) return;
        if (event.key === 'ArrowRight') { event.preventDefault(); show(current + 1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); show(current - 1); }
      });
      zoom.addEventListener('hb:close', () => items.forEach(item => item.classList.remove('is-zoomed')));
    }

    return {
      showMedia(mediaId) {
        if (!mediaId) return;
        const index = slides.findIndex(slide => slide.dataset.mediaId === String(mediaId));
        if (index < 0) return;
        // A lazy slide that is about to be shown (scrolled to, or moved first on desktop) starts loading
        // now, with its neighbours, instead of when the browser notices it.
        const near = slides.slice(Math.max(0, index - 1), index + 2).flatMap(slide => [...slide.querySelectorAll('img')]);
        if (window.HumblerSkeleton) window.HumblerSkeleton.warm(near, near.length);
        else near.forEach(img => { img.loading = 'eager'; });
        slides.forEach((slide, position) => slide.classList.toggle('is-variant-media', position === index));
        go(index);
      }
    };
  }

  /* ---------- description cleanup (pasted admin markup) ---------- */
  const BULLET = /^\s*(?:[o•·▪–-]|\d+[.)])\s+/;
  const INCLUDED_TEXT = /^(included with every humbler pendant|two 22.{0,3} chains|one (cuban|rope) chain)/i;
  // Engraving products: the description's old engraving rules ("*CASE SENSITIVE* It will be engraved exactly how
  // you type it", "1-6 Words Maximum", "No Emojis…") contradict the engraving field, which states the real ones.
  // Only those lines are hidden; the rest of the description stays as written.
  const ENGRAVING_RULES = /case[\s-]*sensitive|\bwords?\s+maximum|\bno\s+emojis?\b/i;
  function cleanCopy(copy, title, engraving = false) {
    copy.querySelectorAll('[style]').forEach(el => el.removeAttribute('style'));
    copy.querySelectorAll('meta').forEach(el => el.remove());
    const text = el => el.textContent.replace(/\s+/g, ' ').trim();

    // Fake bullets ("o …", "• …", "- …") split by <br> or spread over paragraphs become a real list.
    copy.querySelectorAll(':scope > p').forEach(p => {
      const parts = p.innerHTML.split(/<br\s*\/?>/i).map(part => part.replace(/&nbsp;/g, ' ').trim()).filter(part => part.replace(/<[^>]+>/g, '').trim());
      const plain = parts.map(part => part.replace(/<[^>]+>/g, '').trim());
      if (!parts.length || !plain.every(line => BULLET.test(line))) return;
      const list = document.createElement('ul');
      parts.forEach(part => {
        const li = document.createElement('li');
        li.innerHTML = part;
        const walker = document.createTreeWalker(li, NodeFilter.SHOW_TEXT);
        const first = walker.nextNode();
        if (first) first.textContent = first.textContent.replace(BULLET, '');
        list.append(li);
      });
      p.replaceWith(list);
    });
    // Merge neighbouring lists produced above.
    copy.querySelectorAll(':scope > ul + ul').forEach(list => {
      const previous = list.previousElementSibling;
      if (previous?.tagName === 'UL') { previous.append(...list.children); list.remove(); }
    });

    if (engraving) {
      copy.querySelectorAll('li, p').forEach(el => {
        if (!ENGRAVING_RULES.test(text(el))) return;
        const parts = el.innerHTML.split(/<br\s*\/?>/i);
        const kept = parts.filter(part => !ENGRAVING_RULES.test(part.replace(/<[^>]+>/g, ' ')));
        if (parts.length > 1 && kept.length) el.innerHTML = kept.join('<br>');
        else el.remove();
      });
    }

    // The included chains are shown in the value stack, so they are not repeated here.
    if (copy.hasAttribute('data-hb-copy-included')) {
      copy.querySelectorAll('p, li').forEach(el => { if (INCLUDED_TEXT.test(text(el))) el.remove(); });
    }

    // A first line that only repeats the product name (e.g. " Hard Work & God's Work Pendant").
    const first = copy.firstElementChild;
    if (first && first.tagName === 'P') {
      const line = text(first).toLowerCase();
      const words = line.replace(/[^\p{L}\p{N}&' ]/gu, ' ').split(/\s+/).filter(Boolean);
      const titleText = title.toLowerCase();
      if (line && line.length < 70 && words.length && words.every(word => titleText.includes(word) || ['pendant', 'chain', 'bracelet', 'ring'].includes(word))) first.remove();
    }

    copy.querySelectorAll('p, li, strong, span, ul').forEach(el => { if (!el.textContent.trim() && !el.querySelector('img, video, iframe')) el.remove(); });
  }

  /* ---------- product ---------- */
  function initProduct(root) {
    const json = root.querySelector('[data-hb-pdp-json]');
    const form = root.querySelector('form.hb-pdp__form');
    if (!json || !form) return;
    const data = JSON.parse(json.textContent);
    if (data.pair) data.pair.price = fromUsd(data.pair.price);
    // A chain set is a chain's converted $33 three times, as checkout charges it (a converted $99 is a cent apart in some
    // currencies).
    if (data.chains) data.chains.price = fromUsd(Math.round(data.chains.price / (data.chains.count || 3))) * (data.chains.count || 3);
    const S = data.strings || {};
    const gallery = initGallery(root);

    const idInput = form.querySelector('[data-hb-variant-input]');
    const addButton = form.querySelector('[data-hb-add]');
    const addLabel = addButton.querySelector('[data-hb-add-label]');
    const addPrice = addButton.querySelector('[data-hb-add-price]');
    const addSep = addButton.querySelector('[data-hb-add-sep]');
    const statusEl = form.querySelector('[data-hb-status]');
    const priceWrap = root.querySelector('[data-hb-price-wrap]');
    const priceLabel = root.querySelector('[data-hb-price-label]');
    const priceCurrent = root.querySelector('[data-hb-price-current]');
    const priceCompare = root.querySelector('[data-hb-price-compare]');
    const priceCompareValue = root.querySelector('[data-hb-price-compare-value]');
    const saveBadge = root.querySelector('[data-hb-save]');
    const saveValue = root.querySelector('[data-hb-save-value]');
    const termsPreview = root.querySelector('[data-hb-terms] .hb-payment-terms-preview');
    const value = root.querySelector('[data-hb-value]');
    const optionGroups = [...form.querySelectorAll('[data-hb-option]')];
    const offer = form.querySelector('[data-hb-offer]');
    const pickers = [...form.querySelectorAll('[data-hb-picker]')];
    const math = form.querySelector('[data-hb-offer-math]');
    // 2 for $99: the "Add a second item for only $34" row's switch is the pair mode (snippets/humbler-pdp-offer).
    const second = offer?.querySelector('[data-hb-second]');
    const pairSwitch = second?.querySelector('[data-hb-pair-switch]');
    const secondTitle = second?.querySelector('[data-hb-second-title]');
    const secondImage = second?.querySelector('[data-hb-second-media] img');
    const secondDefault = secondImage ? { src: secondImage.getAttribute('src'), srcset: secondImage.getAttribute('srcset') || '' } : null;
    const addon = form.querySelector('[data-hb-addon]');
    const addonInput = addon?.querySelector('[data-hb-addon-input]');
    const addonNote = addon?.querySelector('[data-hb-addon-note]');
    const addonNoteText = addonNote?.textContent || '';
    const sticky = root.querySelector('[data-hb-sticky]');
    const stickyButton = sticky?.querySelector('[data-hb-sticky-button]');
    const guide = root.querySelector('[data-hb-guide]');
    const parse = html => { const el = document.createElement('textarea'); el.innerHTML = html; return el.value; };
    // Engraving mode: the add needs <humbler-engraving>; until it is ready the buttons stay locked (fail closed).
    const engravingMode = data.engraving === true;
    const engraving = engravingMode ? form.querySelector('humbler-engraving') : null;
    let engravingReady = false;

    let variant = data.variants.find(item => item.id === data.selectedVariantId) || data.variants[0];
    let mode = 'single';
    let busy = false;
    let statusTimer = 0;
    const picks = { pair: [], chains: [] };
    const auto = { pair: false, chains: false };

    /* ----- variant ----- */
    const selectedOptions = () => optionGroups.map(group => group.querySelector('[data-hb-option-input]:checked')?.value);
    const matches = (candidate, options) => candidate.options.every((option, index) => options[index] === undefined || options[index] === option);

    function updateAvailability() {
      const current = variant.options;
      optionGroups.forEach((group, index) => {
        group.querySelectorAll('[data-hb-option-input]').forEach(input => {
          const options = current.slice();
          options[index] = input.value;
          const available = data.variants.some(candidate => candidate.available && matches(candidate, optionGroups.length > 1 ? options : [input.value]));
          input.nextElementSibling?.classList.toggle('is-unavailable', !available);
        });
      });
    }

    function setVariant(next, { updateUrl = true } = {}) {
      if (!next) return;
      variant = next;
      idInput.value = next.id;
      optionGroups.forEach((group, index) => {
        const option = next.options[index];
        group.querySelectorAll('[data-hb-option-input]').forEach(input => { input.checked = input.value === option; });
        const label = group.querySelector('[data-hb-option-value]');
        if (label) label.textContent = option;
      });
      updateAvailability();
      renderPrice();
      matchChainLengths();
      renderOffer();
      syncGuide();
      if (next.media_id) gallery.showMedia(next.media_id);
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', next.id);
        window.history.replaceState(window.history.state, '', url);
      }
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      root.dispatchEvent(new CustomEvent('humbler:variant:change', { bubbles: true, detail: { variant: next, product: data.handle } }));
    }

    function onOptionInput(input) {
      const options = selectedOptions();
      const groupIndex = Number(input.closest('[data-hb-option]')?.dataset.hbOption || 0);
      const next = data.variants.find(candidate => matches(candidate, options))
        || data.variants.find(candidate => candidate.options[groupIndex] === input.value && candidate.available)
        || data.variants.find(candidate => candidate.options[groupIndex] === input.value);
      setVariant(next);
    }

    form.addEventListener('change', event => {
      const input = event.target.closest('[data-hb-option-input]');
      if (input) onOptionInput(input);
    });

    /* ----- price + value stack ----- */
    function renderPrice() {
      const onSale = variant.compare_at_price > variant.price;
      if (priceCurrent) priceCurrent.textContent = money(variant.price);
      priceWrap?.classList.toggle('hb-price--sale', onSale);
      if (priceLabel) priceLabel.textContent = onSale ? priceLabel.dataset.sale : priceLabel.dataset.regular;
      if (priceCompare) priceCompare.hidden = !onSale;
      if (priceCompareValue) priceCompareValue.textContent = money(variant.compare_at_price);
      if (saveBadge) saveBadge.hidden = !onSale;
      if (saveValue) saveValue.textContent = onSale ? money(variant.compare_at_price - variant.price) : '';
      root.querySelectorAll('[data-hb-single-price]').forEach(el => { el.textContent = money(variant.price); });
      // "for only $34": the offer price less this variant's, said only while it is under this variant's own price.
      if (secondTitle) {
        const extra = data.pair.price - variant.price;
        secondTitle.textContent = extra > 0 && extra < variant.price ? fill(secondTitle.dataset.only, { price: money(extra) }) : secondTitle.dataset.plain;
      }
      root.querySelectorAll('[data-hb-save-up-to]').forEach(el => {
        const price = el.dataset.hbSaveUpTo === 'pair' ? data.pair.price : data.chains.price;
        const save = variant.price + Number(el.dataset.max || 0) - price;
        if (save > 0) el.textContent = fill(S.saveUpTo, { amount: money(save) });
      });
      if (value) {
        const extra = Number(value.dataset.extra || 0);
        value.querySelectorAll('[data-hb-value-piece], [data-hb-value-yours]').forEach(el => { el.textContent = money(variant.price); });
        const total = value.querySelector('[data-hb-value-total]');
        if (total) total.textContent = money(variant.price + extra);
      }
    }

    // Native Shopify renders {{ form | payment_terms }} itself (it follows the selected variant's
    // price); the preview fills its stand-in the same way. Shop Pay Installments start at $50.
    function renderTerms() {
      if (!termsPreview || !isPreview()) return;
      // Shop Pay Installments: US dollars only.
      const show = variant.available && variant.price >= 5000 && !ui().localized;
      termsPreview.textContent = show ? fill(S.installments, { amount: money(Math.round(variant.price / 4)) }) : '';
    }

    /* ----- offers ----- */
    const addonPrice = () => (addonInput && addonInput.checked && !addonInput.disabled ? Number(addon.dataset.price) : 0);
    const needed = type => (type === 'pair' ? 1 : Math.max(1, (data.chains.count || 3) - 1));

    function quote() {
      const chosen = picks[mode] || [];
      const need = needed(mode);
      const regular = variant.price + chosen.reduce((sum, card) => sum + Number(card.dataset.price || 0), 0);
      const offerPrice = mode === 'pair' ? data.pair.price : data.chains.price;
      const complete = chosen.length >= need;
      const discounted = complete && regular > offerPrice;
      const total = discounted ? offerPrice : regular;
      return { chosen, need, missing: Math.max(0, need - chosen.length), regular, total, discounted, saving: regular - total, offerPrice };
    }

    function buttonState() {
      if (!variant.available) return { label: S.soldOut, disabled: true };
      if (engravingMode && !engravingReady) return { label: S.addToBag, price: money(variant.price), disabled: true };
      if (mode === 'single') return { label: S.addToBag, price: money(variant.price + addonPrice()) };
      const q = quote();
      if (q.missing > 0) return { label: mode === 'pair' ? S.choosePair : fill(S.chooseChains, { count: q.missing }), incomplete: true };
      if (q.discounted) return { label: fill(mode === 'pair' ? S.addPair : S.addChains, { price: money(q.offerPrice) }), price: fill(S.saveAmount, { amount: money(q.saving) }) };
      return { label: mode === 'pair' ? S.addBoth : S.addThree, price: money(q.total + addonPrice()) };
    }

    function renderButton() {
      if (busy) return;
      const state = buttonState();
      addLabel.textContent = state.label;
      addPrice.textContent = state.price || '';
      addPrice.hidden = !state.price;
      addSep.hidden = !state.price;
      addButton.disabled = !!state.disabled;
      addButton.dataset.incomplete = state.incomplete ? 'true' : 'false';
      if (stickyButton) {
        stickyButton.querySelector('[data-hb-sticky-label]').textContent = state.label;
        const stickyPrice = stickyButton.querySelector('[data-hb-sticky-price]');
        stickyPrice.textContent = state.price || '';
        stickyPrice.hidden = !state.price;
        stickyButton.querySelector('[data-hb-sticky-sep]').hidden = !state.price;
        stickyButton.disabled = !!state.disabled;
      }
      renderTerms();
    }

    // One line under the picker: "Set $130 $99 · You save $31".
    function renderMath() {
      if (!math) return;
      const q = mode === 'single' ? null : quote();
      if (!q || q.missing > 0) { math.hidden = true; return; }
      math.hidden = false;
      math.querySelector('[data-hb-math-label]').textContent = mode === 'pair' ? S.setLabel : S.chainsSetLabel;
      math.querySelector('[data-hb-math-regular]').textContent = q.discounted ? money(q.regular) : '';
      math.querySelector('[data-hb-math-total]').textContent = money(q.total);
      const note = math.querySelector('[data-hb-math-note]');
      const addonText = addonPrice() ? `+ ${S.addonShort} ${money(addonPrice())}` : '';
      note.textContent = [q.discounted ? fill(S.youSave, { amount: money(q.saving) }) : fill(S.underPrice, { price: money(q.offerPrice) }), addonText].filter(Boolean).join(' · ');
      note.classList.toggle('is-saving', q.discounted);
    }

    function renderCounts() {
      pickers.forEach(picker => {
        const type = picker.dataset.hbPicker;
        const status = picker.querySelector('[data-hb-picker-count]');
        if (!status) return;
        const missing = needed(type) - picks[type].length;
        status.textContent = missing > 0 ? fill(S.pickedCount, { count: picks[type].length, total: needed(type) }) : auto[type] ? S.pickedForYou : S.pickedByYou;
        status.classList.toggle('is-auto', missing <= 0);
      });
    }

    // The row's thumbnail: the second piece while the pair is on, else the piece the picker would pick first.
    function renderSecond() {
      if (!secondImage) return;
      const picked = mode === 'pair' ? picks.pair[0]?.querySelector('img') : null;
      const src = picked ? picked.currentSrc || picked.src : secondDefault.src;
      if (secondImage.getAttribute('src') === src) return;
      if (picked) secondImage.removeAttribute('srcset');
      else if (secondDefault.srcset) secondImage.setAttribute('srcset', secondDefault.srcset);
      secondImage.src = src;
    }

    function renderOffer() {
      renderCounts();
      renderSecond();
      renderMath();
      renderButton();
      // The "pick your pieces" hint goes once the picks are complete (after the shopper's own pick).
      if (statusEl && statusEl.textContent === S.pickHint && (mode === 'single' || quote().missing <= 0)) clearStatus();
    }

    function setPicked(card, on) {
      card.querySelector('[data-hb-pick-toggle]')?.setAttribute('aria-pressed', String(on));
      const sizes = card.querySelector('[data-hb-pick-sizes]');
      if (sizes) sizes.hidden = !on;
      card.classList.toggle('is-picked', on);
    }

    function togglePick(type, card) {
      const list = picks[type];
      const index = list.indexOf(card);
      auto[type] = false;
      if (index >= 0) {
        list.splice(index, 1);
        setPicked(card, false);
      } else {
        if (list.length >= needed(type)) setPicked(list.shift(), false);
        list.push(card);
        setPicked(card, true);
      }
      renderOffer();
    }

    // Opening an offer tab picks the first eligible piece(s) so the set is one tap from the bag.
    function preselect(type) {
      if (picks[type].length) return;
      const picker = pickers.find(item => item.dataset.hbPicker === type);
      const cards = [...(picker?.querySelectorAll('[data-hb-pick]:not([hidden])') || [])].slice(0, needed(type));
      if (!cards.length) return;
      cards.forEach(card => { picks[type].push(card); setPicked(card, true); });
      auto[type] = true;
    }

    function chooseSize(button) {
      const card = button.closest('[data-hb-pick]');
      card.querySelectorAll('[data-hb-pick-size]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      card.dataset.variantId = button.dataset.variantId;
      card.dataset.price = button.dataset.price;
      card.dataset.option = button.dataset.option;
      card.dataset.touched = 'true';
      const price = card.querySelector('[data-hb-pick-price]');
      if (price) price.textContent = money(button.dataset.price);
      renderOffer();
    }

    // Chain picks follow the length being viewed until the shopper picks a length on that card.
    function matchChainLengths() {
      const option = variant.options[0];
      form.querySelectorAll('[data-hb-picker="chains"] [data-hb-pick]').forEach(card => {
        if (card.dataset.touched === 'true') return;
        const button = [...card.querySelectorAll('[data-hb-pick-size]')].find(item => item.dataset.option === option && !item.disabled);
        if (!button) return;
        card.querySelectorAll('[data-hb-pick-size]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        card.dataset.variantId = button.dataset.variantId;
        card.dataset.price = button.dataset.price;
        card.dataset.option = button.dataset.option;
        const price = card.querySelector('[data-hb-pick-price]');
        if (price) price.textContent = money(button.dataset.price);
      });
    }

    function setMode(next) {
      mode = next;
      // the switch and the segment follow the mode (the pair has no segment option: its switch is the row's)
      if (pairSwitch) pairSwitch.checked = next === 'pair';
      offer?.querySelectorAll('[data-hb-offer-mode]').forEach(radio => { radio.checked = radio.value === next; });
      if (next !== 'single') preselect(next);
      pickers.forEach(picker => {
        picker.hidden = picker.dataset.hbPicker !== next;
        if (!picker.hidden) picker._progress?.();
      });
      clearStatus();
      renderOffer();
    }

    pickers.forEach(picker => {
      const type = picker.dataset.hbPicker;
      const list = picker.querySelector('[data-hb-picker-list]');
      picker._progress = bindProgress(list, picker.querySelector('[data-hb-progress]'));
      picker.addEventListener('click', event => {
        const filter = event.target.closest('[data-hb-filter]');
        if (filter) {
          const category = filter.dataset.hbFilter;
          picker.querySelectorAll('[data-hb-filter]').forEach(item => item.setAttribute('aria-pressed', String(item === filter)));
          picker.querySelectorAll('[data-hb-pick]').forEach(card => { card.hidden = category !== 'all' && card.dataset.category !== category; });
          list.scrollLeft = 0;
          picker._progress();
          return;
        }
        const size = event.target.closest('[data-hb-pick-size]');
        if (size) { chooseSize(size); return; }
        const toggle = event.target.closest('[data-hb-pick-toggle]');
        if (toggle) togglePick(type, toggle.closest('[data-hb-pick]'));
      });
    });
    offer?.querySelectorAll('[data-hb-offer-mode]').forEach(radio => radio.addEventListener('change', () => { if (radio.checked) setMode(radio.value); }));
    pairSwitch?.addEventListener('change', () => setMode(pairSwitch.checked ? 'pair' : 'single'));
    // Intent to open an offer tab (hover, touch, focus) starts loading its first pieces, so the
    // preselected "Picked for you" cards are there when the picker opens.
    [...(offer?.querySelectorAll('[data-hb-offer-mode]') || []), pairSwitch].filter(Boolean).forEach(radio => {
      const picker = pickers.find(item => item.dataset.hbPicker === (radio === pairSwitch ? 'pair' : radio.value));
      if (!picker) return;
      const warm = () => {
        const images = picker.querySelectorAll('[data-hb-pick]:not([hidden]) img');
        if (window.HumblerSkeleton) window.HumblerSkeleton.warm(images, 4);
        else [...images].slice(0, 4).forEach(img => { img.loading = 'eager'; });
      };
      const target = radio.closest('label') || radio;
      ['pointerenter', 'touchstart', 'focusin'].forEach(type => target.addEventListener(type, warm, { once: true, passive: true }));
    });
    addonInput?.addEventListener('change', renderOffer);

    /* ----- add to bag ----- */
    function clearStatus() {
      clearTimeout(statusTimer);
      if (statusEl) { statusEl.textContent = ''; statusEl.classList.remove('is-error', 'hb-sr'); }
    }
    // A visible line stays until the shopper's next change: one that vanished on a timer moved the buy box
    // (trust strip, gift, reels) with no input, a layout shift. Only a visually hidden message clears itself.
    function showStatus(message, isError = false, quiet = false) {
      if (!statusEl) return;
      clearTimeout(statusTimer);
      statusEl.textContent = message;
      statusEl.classList.toggle('is-error', isError);
      statusEl.classList.toggle('hb-sr', quiet);
      if (quiet) statusTimer = setTimeout(clearStatus, 5000);
    }
    function setBusy(on) {
      busy = on;
      [addButton, stickyButton].filter(Boolean).forEach(button => {
        button.classList.toggle('is-loading', on);
        button.setAttribute('aria-busy', String(on));
      });
      if (on) {
        addLabel.textContent = S.adding;
        if (stickyButton) stickyButton.querySelector('[data-hb-sticky-label]').textContent = S.adding;
      } else {
        renderButton();
      }
    }

    function buildItems(engraved) {
      const main = { id: variant.id, quantity: 1, ...(engraved ? { properties: engraved } : {}) };
      let items = [main];
      if (mode === 'pair') {
        const bundleId = uuid();
        const second = picks.pair[0];
        main.properties = { _bundle_id: bundleId, ...data.pair.properties, _bundle_item: '1 of 2' };
        items = [main, { id: Number(second.dataset.variantId), quantity: 1, properties: { _bundle_id: bundleId, ...data.pair.properties, _bundle_item: '2 of 2' } }];
      } else if (mode === 'chains') {
        main.properties = { ...data.chains.properties };
        items = [main, ...picks.chains.map(card => ({ id: Number(card.dataset.variantId), quantity: 1, properties: { ...data.chains.properties } }))];
      }
      if (addonPrice()) items.push({ id: Number(addon.dataset.variantId), quantity: 1 });
      return items;
    }

    // Without the bag script: Shopify's /cart/add accepts items[][…] form fields; return_to applies a code.
    function nativeSubmit(items, options) {
      const base = window.Shopify?.routes?.root || '/';
      const fallback = document.createElement('form');
      fallback.method = 'post';
      fallback.action = `${base}cart/add`;
      fallback.hidden = true;
      const field = (name, fieldValue) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = fieldValue;
        fallback.append(input);
      };
      field('form_type', 'product');
      field('utf8', '✓');
      items.forEach(item => {
        field('items[][id]', item.id);
        field('items[][quantity]', item.quantity || 1);
        Object.entries(item.properties || {}).forEach(([key, property]) => field(`items[][properties][${key}]`, property));
      });
      if (options.discountCode) field('return_to', `${base}discount/${encodeURIComponent(options.discountCode)}?redirect=${encodeURIComponent(`${base}cart`)}`);
      document.body.append(fallback);
      fallback.submit();
    }

    function focusPicker() {
      const picker = pickers.find(item => !item.hidden);
      if (!picker) return;
      picker.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      const first = picker.querySelector('[data-hb-pick]:not([hidden]) [data-hb-pick-toggle]');
      first?.focus({ preventScroll: true });
      showStatus(S.pickHint);
    }

    // The one add routine: the form's submit, the sticky bar and (engraving mode) the add button's click.
    async function attemptAdd() {
      if (busy || !variant.available) return;
      let engraved = null;
      if (engravingMode) {
        if (!engravingReady) { showStatus(S.engravingUnavailable, true); return; }
        // The field shows what is missing or wrong and takes focus; nothing is added.
        const result = engraving.validate({ submit: true, focus: true });
        if (!result.ok) { clearStatus(); return; }
        engraved = { [engraving.key]: result.value };
      }
      if (mode !== 'single' && quote().missing > 0) { focusPicker(); return; }
      const items = buildItems(engraved);
      const options = { open: true, source: mode === 'pair' ? 'pdp-pair' : mode === 'chains' ? 'chains' : 'pdp' };
      if (mode === 'chains' && data.chains.discountCode) options.discountCode = data.chains.discountCode;
      clearStatus();
      if (!window.HumblerCart?.add) {
        if (isPreview()) { showStatus(S.previewCart); return; }
        setBusy(true);
        nativeSubmit(items, options);
        return;
      }
      setBusy(true);
      try {
        const result = await window.HumblerCart.add(items, options);
        if (result && result.ok === false) throw new Error(result.error || S.error);
        // The bag opens and announces the add in its own status region; without one, say it here (screen
        // readers only: the open bag is the visible confirmation).
        if (!document.querySelector('[data-hb-cart-status]')) showStatus(S.added, false, true);
        if (addonInput?.checked) { addonInput.checked = false; }
      } catch (error) {
        showStatus((error && error.message) || S.error, true);
      } finally {
        setBusy(false);
        renderOffer();
      }
    }
    form.addEventListener('submit', event => {
      event.preventDefault();
      attemptAdd();
    });
    window.addEventListener('pageshow', event => { if (event.persisted) setBusy(false); });

    /* ----- engraving mode: unlock once <humbler-engraving> is ready; refuse to add without it ----- */
    if (engravingMode) {
      const unlock = () => [addButton, stickyButton].forEach(button => button?.removeAttribute('data-hb-locked'));
      let lockTimer = 0;
      const markReady = () => {
        if (engravingReady || !engraving?.ready || typeof engraving.validate !== 'function') return;
        engravingReady = true;
        clearTimeout(lockTimer);
        unlock();
        if (statusEl?.textContent === S.engravingUnavailable) clearStatus();
        renderButton();
      };
      // Still not ready after 5s (the script failed to load): say so; the buttons show as disabled.
      lockTimer = setTimeout(() => {
        if (engravingReady) return;
        unlock();
        showStatus(S.engravingUnavailable, true);
      }, 5000);
      form.addEventListener('humbler:engraving:ready', markReady);
      window.customElements?.whenDefined('humbler-engraving').then(markReady);
      markReady();
      // The add button's clicks are taken here, in the capture phase, before any listener on the button itself:
      // an app that rewires the button (Easify sets type="button" and adds through its own click handler) can
      // neither add on its own nor add twice, and its form fields are never read.
      form.addEventListener('click', event => {
        if (!event.target.closest?.('[data-hb-add]')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        attemptAdd();
      }, true);
    }

    /* ----- Mystery add-on: disable when the bag already holds one ----- */
    document.addEventListener('humbler:cart:updated', event => {
      if (!addon || !addonInput) return;
      const items = event.detail?.cart?.items || [];
      const inBag = items.some(item => item.handle === addon.dataset.handle || String(item.variant_id ?? item.id) === addon.dataset.variantId);
      addonInput.disabled = inBag;
      if (inBag) addonInput.checked = false;
      if (addonNote) addonNote.textContent = inBag ? S.mysteryInBag : addonNoteText;
      renderOffer();
    });

    /* ----- floating add to bag (mobile): once the main button has scrolled up under the header ----- */
    if (sticky && stickyButton && 'IntersectionObserver' in window) {
      sticky.hidden = false;
      sticky.inert = true;
      let offscreen = false;
      const update = () => {
        const show = offscreen && !desktop.matches && !anyDialogOpen();
        sticky.classList.toggle('is-visible', show);
        sticky.inert = !show;
        stickyButton.tabIndex = show ? 0 : -1;
        document.documentElement.classList.toggle('hb-pdp-sticky-on', offscreen && !desktop.matches);
      };
      // "On screen" = the whole button visible below the sticky header; otherwise the bar covers the cut-off part.
      // The root runs far below the fold, so a button not yet scrolled to counts as on screen: on first paint
      // the bar stays down and leaves the offer selector above the button uncovered.
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hb-header-height')) || 56;
      new IntersectionObserver(([entry]) => { offscreen = !entry.isIntersecting || entry.intersectionRatio < 0.98; update(); }, { rootMargin: `-${Math.round(headerHeight)}px 0px 10000px 0px`, threshold: [0, 0.5, 0.98, 1] }).observe(addButton);
      // Hidden while the bag (or any drawer) is open; dialogs announce hb:open / hb:close and close.
      document.addEventListener('hb:open', () => requestAnimationFrame(update), true);
      document.addEventListener('hb:close', () => requestAnimationFrame(update), true);
      document.addEventListener('close', () => requestAnimationFrame(update), true);
      desktop.addEventListener?.('change', update);
      stickyButton.addEventListener('click', () => {
        if (engravingMode) { attemptAdd(); return; }
        if (addButton.dataset.incomplete === 'true') { focusPicker(); return; }
        // Easify turns the add button into type="button" and adds through its own click handler; requestSubmit
        // with a submitter that is not a submit button throws, so click it instead.
        if (typeof form.requestSubmit === 'function' && addButton.type === 'submit') form.requestSubmit(addButton.disabled ? undefined : addButton);
        else addButton.click();
      });
    }

    /* ----- desktop: an info column taller than the window scrolls with the page and sticks by the edge it
       reaches: its bottom on the way down, its top (title, price) on the way back up. Only the scroll
       direction is tracked here; the browser does the sticking (see .hb-pdp__info in the CSS). ----- */
    const info = root.querySelector('[data-hb-pdp-info]');
    if (info) {
      let direction = 'down';
      let anchor = null; // the column's page offset when the direction last changed
      let lastY = window.scrollY;
      const clear = () => {
        anchor = null;
        info.removeAttribute('data-hb-scroll');
        ['--hb-pdp-top', '--hb-pdp-bottom', '--hb-pdp-shift'].forEach(name => info.style.removeProperty(name));
      };
      // Re-anchors the column where it is now (no visible move), then lets it stick at the edge ahead.
      const place = (keepAnchor = false) => {
        if (!desktop.matches) { clear(); return; }
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hb-header-height')) || 64;
        const top = header + 16;
        const height = info.offsetHeight;
        if (top + height + 24 <= window.innerHeight) { clear(); return; }
        const cell = info.parentElement;
        const start = cell.getBoundingClientRect().top + parseFloat(getComputedStyle(cell).paddingTop);
        // How far the column can travel beside the gallery; the shift never makes the grid row taller.
        const room = Math.max(0, (cell.querySelector('[data-hb-gallery]')?.offsetHeight || 0) - height);
        if (!keepAnchor || anchor === null) anchor = info.getBoundingClientRect().top + window.scrollY;
        const travelled = Math.min(room, Math.max(0, Math.floor(anchor - window.scrollY - start)));
        info.dataset.hbScroll = direction;
        if (direction === 'down') {
          info.style.setProperty('--hb-pdp-shift', `${travelled}px`);
          info.style.setProperty('--hb-pdp-top', `${Math.round(window.innerHeight - height - 24)}px`);
        } else {
          info.style.setProperty('--hb-pdp-shift', `${room - travelled}px`);
          info.style.setProperty('--hb-pdp-bottom', `${Math.round(window.innerHeight - top - height)}px`);
        }
      };
      window.addEventListener('scroll', () => {
        const y = window.scrollY;
        const next = y > lastY ? 'down' : y < lastY ? 'up' : direction;
        lastY = y;
        if (next === direction) return;
        direction = next;
        place();
      }, { passive: true });
      // A details row opening or a picker appearing: the top stays put on the way up; on the way down the
      // column is held where it is rather than jumping to pin its new bottom. The gallery is watched too: its
      // height changes as its images load in.
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(() => place(direction === 'up'));
        [info, info.parentElement.querySelector('[data-hb-gallery]')].forEach(item => item && observer.observe(item));
      }
      window.addEventListener('resize', () => place());
      desktop.addEventListener?.('change', () => place());
      place();
    }

    /* ----- size guide ----- */
    function syncGuide() {
      guide?.querySelectorAll('[data-hb-guide-pick]').forEach(row => row.setAttribute('aria-pressed', String(row.dataset.hbGuidePick === variant.options[Number(row.dataset.hbGuideOption) || 0])));
    }
    root.querySelectorAll('[data-hb-guide-open]').forEach(button => button.addEventListener('click', () => openDialog(guide, button)));
    guide?.addEventListener('click', event => {
      const row = event.target.closest('[data-hb-guide-pick]');
      const index = row?.dataset.hbGuideOption || '0';
      const group = optionGroups.find(item => item.dataset.hbOption === index);
      if (!row || !group) return;
      const input = [...group.querySelectorAll('[data-hb-option-input]')].find(item => item.value === row.dataset.hbGuidePick);
      if (input) { input.checked = true; onOptionInput(input); }
      // One list: the pick answers it. Several (a set's bracelet size and necklace length): stay open for the next.
      if (guide.querySelector(`[data-hb-guide-option]:not([data-hb-guide-option="${index}"])`)) syncGuide();
      else closeDialog(guide);
    });

    /* ----- Shop Pay installments: the reserved line (.is-pending, see the section) goes once Shopify has
       drawn <shopify-payment-terms>, or after 6s if it never does (no installments on this store). ----- */
    const terms = root.querySelector('[data-hb-terms].is-pending');
    if (terms) {
      const element = terms.querySelector('shopify-payment-terms');
      let observer = null;
      let timer = 0;
      const settle = () => { terms.classList.remove('is-pending'); observer?.disconnect(); clearTimeout(timer); };
      const drawn = () => element && element.getBoundingClientRect().height > 0;
      if (!element || isPreview() || drawn()) settle();
      else {
        if ('ResizeObserver' in window) observer = new ResizeObserver(() => { if (drawn()) settle(); });
        observer?.observe(element);
        timer = setTimeout(settle, 6000);
      }
    }

    /* ----- reviews link: only when the reviews section is on the page ----- */
    root.querySelectorAll('[data-hb-reviews-link]').forEach(link => {
      if (!document.getElementById('hb-pdp-reviews')) link.removeAttribute('href');
    });

    /* ----- description ----- */
    root.querySelectorAll('[data-hb-copy]').forEach(copy => cleanCopy(copy, parse(data.title || ''), engravingMode));

    /* ----- reels section (shared): a video of this piece, if there is one, leads the row ----- */
    document.querySelectorAll('[data-hb-reels] .hb-reels__list').forEach(list => {
      const own = [...list.children].find(item => (item.querySelector('[data-hb-reel]')?.dataset.productUrl || '').split('?')[0] === data.url);
      if (!own || own === list.firstElementChild) return;
      list.prepend(own);
      // scroll snapping would otherwise stay on the tile that used to be first
      list.scrollLeft = 0;
      requestAnimationFrame(() => { list.scrollLeft = 0; });
    });

    /* ----- initial state (the preview has no selected_variant: honour ?variant=) ----- */
    const requested = Number(new URL(window.location.href).searchParams.get('variant'));
    const fromUrl = requested && data.variants.find(item => item.id === requested);
    if (fromUrl && fromUrl.id !== variant.id) setVariant(fromUrl, { updateUrl: false });
    else { updateAvailability(); renderPrice(); matchChainLengths(); renderOffer(); syncGuide(); }
    // A page the browser rebuilt on Back can bring the offer inputs back checked (the inputs opt out, not every
    // browser listens): start in the mode they show, so the button adds what the switch says.
    const restored = pairSwitch?.checked ? 'pair' : offer?.querySelector('[data-hb-offer-mode][value="chains"]:checked') ? 'chains' : '';
    if (restored) setMode(restored);
  }

  const init = () => {
    document.querySelectorAll('[data-hb-pdp]:not([data-hb-ready])').forEach(root => {
      root.setAttribute('data-hb-ready', '');
      try { initProduct(root); } catch (error) { console.error('[humbler-product]', error); }
    });
    // Rails sections (Complete the set, You may also like).
    document.querySelectorAll('[data-hb-rail]:not([data-hb-ready])').forEach(rail => {
      rail.setAttribute('data-hb-ready', '');
      bindProgress(rail.querySelector('[data-hb-rail-list]'), rail.querySelector('[data-hb-progress]'));
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
